import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ConfirmPopover } from '../ConfirmPopover'
import { useMediaSrc } from '../useMediaSrc'
import { fanLabel, type Fan, type FanList } from './collectionsData'
import { lastSeenText } from './FanCard'
import { ListMenu } from './ListMenu'

interface Props {
  fan: Fan
  /** Lists loaded on the page (to know which accept or release fans). */
  lists: FanList[]
  canWrite: boolean
  onClose: () => void
  onMessage: () => void
  onAddTo: (lists: FanList[]) => void
  onRemoveFrom: (listId: string) => void
  onRestrict: () => void
  onCopyLink: () => void
}

/** FanSummary (decision 18): who the fan is, what they spent, their lists, and Message. */
export function FanPanel({ fan, lists, canWrite, onClose, onMessage, onAddTo, onRemoveFrom, onRestrict, onCopyLink }: Props) {
  const { t, i18n } = useTranslation('library')
  const banner = useMediaSrc(fan.header)
  const avatar = useMediaSrc(fan.avatar)
  const [menu, setMenu] = useState<'add' | 'more' | 'restrict' | null>(null)
  const label = fanLabel(fan)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !menu && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menu, onClose])

  const memberOf = fan.lists.filter((l) => l.hasUser)
  const removable = (id: string) => lists.find((l) => l.id === id)?.canManageUsers ?? false
  const status = fan.blocked ? 'blocked' : fan.restricted ? 'restricted' : fan.status
  const seen = lastSeenText(fan.lastSeen, i18n.language)
  const since = fan.subscribedAt
    ? new Date(fan.subscribedAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' })
    : null

  return (
    <>
      <div className="lib-sheet-backdrop" onClick={onClose} />
      <aside className="lib-fanpanel" role="dialog" aria-label={label}>
        <div ref={banner.ref as React.RefObject<HTMLDivElement>} className="lib-fanpanel-banner">
          {banner.src && <img src={banner.src} alt="" />}
          <span className="lib-grip" aria-hidden="true" />
          <button type="button" className="lib-fanpanel-close" aria-label={t('close')} onClick={onClose}>
            <FeatherIcon name="x" size={16} />
          </button>
          <span ref={avatar.ref as React.RefObject<HTMLSpanElement>} className="lib-fanpanel-avatar">
            {avatar.src ? <img src={avatar.src} alt="" /> : label.slice(0, 1).toUpperCase()}
          </span>
        </div>
        <div className="lib-fanpanel-body">
          <div className="lib-fanpanel-who">
            <span className="lib-fanpanel-name">
              {label}
              {fan.verified && <FeatherIcon name="check-circle" size={15} />}
            </span>
            <span className="lib-fanpanel-handle">
              @{fan.username}
              {fan.displayName && fan.name && fan.displayName !== fan.name ? ` · ${fan.name}` : ''}
            </span>
            <span className="lib-fanpanel-status">
              {status && <span className="lib-pill">{t(`collections.status.${status}`)}</span>}
              {seen && <span className="lib-fanpanel-seen">{t('collections.lastSeen', { when: seen })}</span>}
            </span>
          </div>
          <div className="lib-fanpanel-actions">
            <button type="button" className="lib-btn lib-btn--primary lib-fanpanel-message" onClick={onMessage}>
              <FeatherIcon name="message-square" size={15} />
              {t('collections.message')}
            </button>
            <button type="button" className="lib-icon-btn lib-icon-btn--boxed" aria-label={t('collections.copyLink')} title={t('collections.copyLink')} onClick={onCopyLink}>
              <FeatherIcon name="link" size={15} />
            </button>
            {canWrite && !fan.restricted && (
              <div className="lib-anchor">
                <button type="button" className="lib-icon-btn lib-icon-btn--boxed" aria-label={t('collections.more')} onClick={() => setMenu(menu === 'more' ? null : 'more')}>
                  <FeatherIcon name="more-horizontal" size={15} />
                </button>
                {menu === 'more' && (
                  <>
                    <div className="lib-backdrop" onClick={() => setMenu(null)} />
                    <div className="lib-pop lib-menu" role="menu" aria-label={t('collections.more')}>
                      <button type="button" role="menuitem" className="lib-menu-item" onClick={() => setMenu('restrict')}>
                        <span className="lib-menu-label">{t('collections.restrict')}</span>
                      </button>
                    </div>
                  </>
                )}
                {menu === 'restrict' && (
                  <ConfirmPopover
                    title={t('collections.restrictTitle', { name: label })}
                    body={t('collections.restrictBody')}
                    confirmLabel={t('collections.restrict')}
                    onConfirm={() => {
                      setMenu(null)
                      onRestrict()
                    }}
                    onCancel={() => setMenu(null)}
                  />
                )}
              </div>
            )}
          </div>
          <dl className="lib-fanpanel-facts">
            {fan.spent !== null && (
              <div>
                <dt>{t('collections.spent')}</dt>
                <dd>{fan.spent.toLocaleString(i18n.language, { style: 'currency', currency: 'USD' })}</dd>
              </div>
            )}
            {since && (
              <div>
                <dt>{t('collections.subscribedSince')}</dt>
                <dd>{since}</dd>
              </div>
            )}
          </dl>
          <section className="lib-fanpanel-lists">
            <span className="lib-fanpanel-label" id={`fan-lists-${fan.id}`}>
              {t('collections.inLists', { n: memberOf.length })}
            </span>
            <ul className="lib-fanpanel-chips" aria-labelledby={`fan-lists-${fan.id}`}>
              {memberOf.map((l) => (
                <li key={l.id} className="lib-fanpanel-chip">
                  {l.name}
                  {canWrite && removable(l.id) && (
                    <button type="button" aria-label={t('collections.removeFromNamed', { name: l.name })} onClick={() => onRemoveFrom(l.id)}>
                      <FeatherIcon name="x" size={12} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {canWrite && (
              <div className="lib-anchor">
                <button type="button" className="lib-btn lib-btn--ghost" onClick={() => setMenu(menu === 'add' ? null : 'add')}>
                  <FeatherIcon name="plus" size={13} />
                  {t('collections.addTo')}
                </button>
                {menu === 'add' && (
                  <ListMenu
                    title={t('collections.addTitle', { count: 1 })}
                    lists={lists}
                    fans={[fan]}
                    onAdd={(ls) => {
                      setMenu(null)
                      onAddTo(ls)
                    }}
                    onClose={() => setMenu(null)}
                  />
                )}
              </div>
            )}
          </section>
        </div>
      </aside>
    </>
  )
}
